/**
 * firebase.js — Lazy Firebase Auth bootstrap for the admin panel.
 *
 * Two deliberate choices here:
 *
 *   • Lazy. The SDK is imported dynamically on first use, so a shopper browsing
 *     the storefront never downloads the auth library at all.
 *   • Non-fatal when unconfigured. If the VITE_FIREBASE_* variables are missing
 *     the module reports that clearly instead of throwing at import time, which
 *     lets the public site work perfectly while the admin route explains exactly
 *     which variables need setting.
 *
 * The values below are Firebase *client* config. They are public identifiers, not
 * secrets — access is controlled by Firestore/Storage rules and by the server's
 * own token verification, never by hiding this config.
 */

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const REQUIRED_KEYS = ['apiKey', 'authDomain', 'projectId', 'appId'];

/** Config keys that are absent, so the UI can name them precisely. */
export const missingConfigKeys = REQUIRED_KEYS.filter((key) => !config[key]).map(
  (key) => `VITE_FIREBASE_${key.replace(/[A-Z]/g, (c) => `_${c}`).toUpperCase()}`,
);

/** True when there is enough config to attempt sign-in. */
export const isFirebaseConfigured = missingConfigKeys.length === 0;

let authPromise = null;

/**
 * Resolve the shared Auth instance, importing and initialising on first call.
 * @returns {Promise<import('firebase/auth').Auth>}
 */
export function getFirebaseAuth() {
  if (!isFirebaseConfigured) {
    return Promise.reject(
      new Error(`Firebase is not configured. Missing environment variables: ${missingConfigKeys.join(', ')}`),
    );
  }

  authPromise ??= (async () => {
    const [{ initializeApp, getApps }, { getAuth, setPersistence, browserLocalPersistence }] = await Promise.all([
      import('firebase/app'),
      import('firebase/auth'),
    ]);

    const app = getApps().length ? getApps()[0] : initializeApp(config);
    const auth = getAuth(app);

    // Survive a page refresh so an admin is not signed out by every reload.
    await setPersistence(auth, browserLocalPersistence).catch(() => {
      /* Private browsing can block storage; an in-memory session still works. */
    });

    return auth;
  })();

  return authPromise;
}

/**
 * Translate a Firebase auth error code into something a person can act on.
 * The generic 'invalid-credential' code covers wrong password, unknown email and
 * several other cases, so the copy stays deliberately non-committal about which.
 */
export function describeAuthError(error) {
  const code = error?.code || '';
  return (
    {
      'auth/invalid-email': 'That does not look like a valid email address.',
      'auth/user-disabled': 'This account has been disabled.',
      'auth/user-not-found': 'No account matches that email and password.',
      'auth/wrong-password': 'No account matches that email and password.',
      'auth/invalid-credential': 'No account matches that email and password.',
      'auth/too-many-requests': 'Too many attempts. Wait a minute and try again.',
      'auth/network-request-failed': 'Network problem — check your connection and retry.',
      'auth/operation-not-allowed':
        'Email/password sign-in is disabled for this Firebase project. Enable it under Authentication → Sign-in method.',
      'auth/configuration-not-found':
        'Firebase Authentication is not set up for this project yet. Enable Email/Password sign-in in the Firebase console.',
    }[code] || error?.message || 'Could not sign in. Please try again.'
  );
}
